// /commissions/settlements — Commission Settlements (PAGES.md §3.59)
//
// View and process commission settlements. Shows calculated commissions per
// staff for the selected period. OWNER+ can calculate and mark as settled/paid.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  PageContainer,
  ErrorBoundary,
  Select,
  Button,
  Input,
  DataTable,
  Badge,
  StatCard,
  DatePicker,
  Skeleton,
  EmptyState,
  Modal,
  RadioGroup,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef, DateRange } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { rupeeInputToPaise } from '@/utils/price-input';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { toLocalIsoDate } from './date-utils';
import { useAuthStore } from '@/stores/use-auth-store';
import { userService } from '@/services/user.service';
import { locationService } from '@/services/location.service';
import {
  useSettlements,
  useSettlementStats,
  useCalculateCommissions,
  useSettleCommission,
  useCancelSettlement,
  useAdjustSettlement,
  useSettlementAdjustments,
} from '@/features/commissions';
import type { CommissionPaymentMode, CommissionSettlement, SettlementStatus } from '@/features/commissions';

const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100, ADMIN: 80, MODERATOR: 60, OWNER: 50, MANAGER: 30, CASHIER: 5,
};

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'PAID', label: 'Paid' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

// FR-STAFF-012 — the modes a commission can be paid in.
const PAYMENT_MODE_OPTIONS: { value: CommissionPaymentMode; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'SALARY_DEDUCTION', label: 'Salary Deduction' },
];

function statusBadgeVariant(status: SettlementStatus): 'pending' | 'success' | 'voided' {
  switch (status) {
    case 'PENDING': return 'pending';
    case 'PAID': return 'success';
    case 'CANCELLED': return 'voided';
  }
}

type Row = CommissionSettlement;

// FR-STAFF-011 — a return reverses commission as a negative adjustment, so the
// payable figure is net_amount, not the gross commission_amount.
export function payableAmount(row: Row): number {
  return Number(row.net_amount ?? row.commission_amount);
}

// adjustments_total holds return reversals AND manual adjustments; split them so
// the Commission column can say which is which.
export function adjustmentParts(row: Row): { returns: number; manual: number } {
  const manual = Number(row.manual_adjustments_total ?? 0);
  return { returns: Number(row.adjustments_total ?? 0) - manual, manual };
}

// Normalize an inverted date range (e.g. from a hand-edited ?from=…&to=… URL)
// so from ≤ to. An inverted range maps to the unsatisfiable SQL filter
// period_start >= from AND period_end <= to, which silently returns an empty
// list — indistinguishable from a legitimately empty period (#164).
export function normalizeDateRange(from: string, to: string): [string, string] {
  if (from && to && from > to) return [to, from];
  return [from, to];
}

function SettlementsContent() {
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const role = useAuthStore((s) => s.profile?.role ?? '');
  const roleLevel = ROLE_LEVELS[role] ?? 0;
  const canSettle = roleLevel >= ROLE_LEVELS.OWNER;

  const page = Number(searchParams.get('page') ?? '1');
  const status = (searchParams.get('status') ?? '') as SettlementStatus | '';
  // Normalize an inverted ?from=…&to=… range so from ≤ to — otherwise the
  // backend gets the unsatisfiable period_start >= from AND period_end <= to
  // and silently returns an empty list (#164).
  const [startDate, endDate] = normalizeDateRange(
    searchParams.get('from') ?? '',
    searchParams.get('to') ?? '',
  );

  // No commission period can be in the future, so disable future dates in both
  // pickers (#164). Memoized so the prop identity is stable across renders.
  const maxDate = useMemo(() => new Date(), []);

  const { data, isLoading, isFetching } = useSettlements({ page, status, start_date: startDate || undefined, end_date: endDate || undefined });
  const rows = useMemo<Row[]>(() => data?.data ?? [], [data]);

  // KPI totals come from a period-scoped stats endpoint that ignores the
  // status filter and pagination — otherwise filtering by Paid would zero out
  // "Total Payable" / "Pending Count" even while pending settlements exist (#161).
  const { data: stats, isLoading: statsLoading } = useSettlementStats({
    start_date: startDate || undefined,
    end_date: endDate || undefined,
  });
  const pendingTotal = stats?.total_payable ?? 0;
  const paidTotal = stats?.already_settled ?? 0;
  const pendingCount = stats?.pending_count ?? 0;

  const calculateMutation = useCalculateCommissions();
  const settleMutation = useSettleCommission();
  const cancelMutation = useCancelSettlement();
  const adjustMutation = useAdjustSettlement();

  // Searchable pickers — staff/managers should never need raw UUIDs
  const { data: locationsData } = useQuery({
    queryKey: ['commission-settlements-locations-picker'],
    queryFn: () => locationService.list({ limit: 100, is_active: 'true' }),
    staleTime: 60_000,
  });
  const locationOptions = useMemo(
    () =>
      (locationsData?.data ?? []).map((l) => ({
        value: l.id,
        label: l.location_code ? `${l.name} — ${l.location_code}` : l.name,
      })),
    [locationsData],
  );

  const { data: usersData } = useQuery({
    queryKey: ['commission-settlements-users-picker'],
    queryFn: () => userService.list({ limit: 200, is_active: 'true' }),
    staleTime: 60_000,
  });
  const userOptions = useMemo(
    () => [
      { value: '', label: 'All staff at location' },
      ...(usersData?.data ?? []).map((u) => ({
        value: u.id,
        label: `${u.name} — ${u.role}`,
      })),
    ],
    [usersData],
  );

  const [showCalc, setShowCalc] = useState(false);
  const [calcLocationId, setCalcLocationId] = useState('');
  const [calcDateRange, setCalcDateRange] = useState<DateRange | undefined>(undefined);
  const [calcUserId, setCalcUserId] = useState('');

  // Mark Paid modal — captures the payment reference so settled commissions are
  // never recorded with payment_reference = null (#162, payroll audit trail).
  const [settleRow, setSettleRow] = useState<Row | null>(null);
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentMode, setPaymentMode] = useState<CommissionPaymentMode>('CASH');
  const [settleError, setSettleError] = useState<string | undefined>(undefined);

  // Adjust modal — an up/down correction of a pending settlement, with a reason.
  const [adjustRow, setAdjustRow] = useState<Row | null>(null);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustError, setAdjustError] = useState<string | undefined>(undefined);
  const { data: adjustHistory } = useSettlementAdjustments(adjustRow?.id);

  // Cancel settlement modal (voids a pending payout).
  const [cancelRow, setCancelRow] = useState<Row | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  // Period date picker for filter
  const filterDateRange = useMemo<DateRange | undefined>(() => {
    if (!startDate || !endDate) return undefined;
    return { from: new Date(startDate), to: new Date(endDate) };
  }, [startDate, endDate]);

  function setParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    if (key !== 'page') params.set('page', '1');
    setSearchParams(params);
  }

  function handleFilterDateChange(range: DateRange) {
    const params = new URLSearchParams(searchParams);
    params.set('from', toLocalIsoDate(range.from));
    params.set('to', toLocalIsoDate(range.to));
    params.set('page', '1');
    setSearchParams(params);
  }

  // Keyboard: c → calculate modal (OWNER+)
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'c' && canSettle) { e.preventDefault(); setShowCalc(true); }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [canSettle]);

  const handleCalculate = useCallback(() => {
    if (!calcLocationId) { toast({ variant: 'warning', title: 'Location required' }); return; }
    if (!calcDateRange) { toast({ variant: 'warning', title: 'Date range required' }); return; }
    const startIso = toLocalIsoDate(calcDateRange.from);
    const endIso = toLocalIsoDate(calcDateRange.to);
    calculateMutation.mutate(
      { location_id: calcLocationId, start_date: startIso, end_date: endIso, user_id: calcUserId || undefined },
      {
        onSuccess: (result) => {
          toast({ variant: 'success', title: 'Commissions calculated', description: `${result.settlements.length} staff — ${formatPaise(result.total_commission, currency)} total` });
          setShowCalc(false);
        },
        onError: (err) => { toast({ variant: 'error', title: 'Failed', description: (err as Error).message }); },
      },
    );
  }, [calcLocationId, calcDateRange, calcUserId, calculateMutation, toast, currency]);

  const openSettle = useCallback((row: Row) => {
    setSettleRow(row);
    setPaymentReference('');
    setPaymentMode('CASH');
    setSettleError(undefined);
  }, []);

  const handleSettle = useCallback(() => {
    if (!settleRow) return;
    const reference = paymentReference.trim();
    if (!reference) {
      setSettleError('Payment reference is required — e.g. UTR, cheque no., or transfer id.');
      return;
    }
    settleMutation.mutate(
      { id: settleRow.id, input: { status: 'PAID' as const, payment_reference: reference, payment_mode: paymentMode } },
      {
        onSuccess: () => {
          toast({ variant: 'success', title: 'Commission settled' });
          setSettleRow(null);
        },
        onError: (err) => { toast({ variant: 'error', title: 'Failed', description: (err as Error).message }); },
      },
    );
  }, [settleRow, paymentReference, paymentMode, settleMutation, toast]);

  const openAdjust = useCallback((row: Row) => {
    setAdjustRow(row);
    setAdjustAmount('');
    setAdjustReason('');
    setAdjustError(undefined);
  }, []);

  const handleAdjust = useCallback(() => {
    if (!adjustRow) return;
    const delta = rupeeInputToPaise(adjustAmount, currency);
    if (delta === null || delta === 0) {
      setAdjustError('Enter a non-zero amount — negative lowers the payable.');
      return;
    }
    const reason = adjustReason.trim();
    if (!reason) {
      setAdjustError('A reason is required.');
      return;
    }
    adjustMutation.mutate(
      { id: adjustRow.id, input: { delta, reason } },
      {
        onSuccess: () => {
          toast({ variant: 'success', title: 'Settlement adjusted' });
          setAdjustRow(null);
        },
        onError: (err) => { toast({ variant: 'error', title: 'Failed', description: (err as Error).message }); },
      },
    );
  }, [adjustRow, adjustAmount, adjustReason, adjustMutation, toast, currency]);

  const openCancel = useCallback((row: Row) => {
    setCancelRow(row);
    setCancelReason('');
  }, []);

  const handleCancel = useCallback(() => {
    if (!cancelRow) return;
    cancelMutation.mutate(
      { id: cancelRow.id, reason: cancelReason.trim() || undefined },
      {
        onSuccess: () => {
          toast({ variant: 'success', title: 'Settlement cancelled' });
          setCancelRow(null);
        },
        onError: (err) => { toast({ variant: 'error', title: 'Failed', description: (err as Error).message }); },
      },
    );
  }, [cancelRow, cancelReason, cancelMutation, toast]);

  const columns: ColumnDef<Row>[] = useMemo(() => [
    {
      key: 'user_id' as keyof Row,
      header: 'Staff',
      render: (_v: unknown, row: Row) => (
        <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100">{row.user_name ?? row.user_id.slice(0, 8)}</p>
      ),
    },
    {
      key: 'period_start' as keyof Row,
      header: 'Period',
      render: (_v: unknown, row: Row) => (
        <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums font-mono">{row.period_start} → {row.period_end}</span>
      ),
    },
    {
      key: 'bill_count' as keyof Row,
      header: 'Bills',
      numeric: true,
      render: (_v: unknown, row: Row) => <span className="text-sm tabular-nums">{row.bill_count}</span>,
    },
    {
      key: 'gross_sales' as keyof Row,
      header: 'Sales',
      numeric: true,
      render: (_v: unknown, row: Row) => <span className="text-sm font-bold tabular-nums">{formatPaise(row.gross_sales, currency)}</span>,
    },
    {
      key: 'commission_rate_pct' as keyof Row,
      header: 'Rate',
      numeric: true,
      render: (_v: unknown, row: Row) => <span className="text-sm tabular-nums">{row.commission_rate_pct}%</span>,
    },
    {
      key: 'commission_amount' as keyof Row,
      header: 'Commission',
      numeric: true,
      render: (_v: unknown, row: Row) => (
        <div className="text-right">
          <span className="text-sm font-bold text-violet-700 dark:text-violet-300 tabular-nums">{formatPaise(payableAmount(row), currency)}</span>
          {Number(row.adjustments_total ?? 0) !== 0 ? (
            <p className="text-xs text-danger-600 dark:text-danger-400 tabular-nums">
              {formatPaise(row.commission_amount, currency)}
              {adjustmentParts(row).returns !== 0 ? <> − {formatPaise(Math.abs(adjustmentParts(row).returns), currency)} returns</> : null}
              {adjustmentParts(row).manual !== 0 ? <> {adjustmentParts(row).manual > 0 ? '+' : '−'} {formatPaise(Math.abs(adjustmentParts(row).manual), currency)} adjusted</> : null}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'status' as keyof Row,
      header: 'Status',
      render: (_v: unknown, row: Row) => (
        <Badge variant={statusBadgeVariant(row.status)} size="sm" dot>
          {row.status}
        </Badge>
      ),
    },
    ...(canSettle ? [{
      key: 'actions' as keyof Row,
      header: '',
      render: (_v: unknown, row: Row) => row.status === 'PENDING' ? (
        <div className="flex items-center gap-1.5 justify-end">
          <Button variant="primary" onClick={() => openSettle(row)} className="text-xs px-2 py-1">
            Mark Paid
          </Button>
          <Button variant="secondary" onClick={() => openAdjust(row)} className="text-xs px-2 py-1">
            Adjust
          </Button>
          <Button variant="ghost" onClick={() => openCancel(row)} className="text-xs px-2 py-1 !text-danger-600 hover:!bg-danger-50 dark:hover:!bg-danger-900/20">
            Cancel
          </Button>
        </div>
      ) : null,
    }] : []),
  ], [canSettle, openSettle, openAdjust, openCancel, currency]);

  // KPI icons
  const payableIcon = (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  );
  const settledIcon = (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  );
  const pendingIcon = (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  );

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(139,92,246,0.7)]">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/15 blur-3xl" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
              Payroll
            </span>
            <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">Commission Settlements</h1>
            <p className="mt-1.5 text-sm text-white/90 max-w-xl">Calculate and settle staff commissions by period and location.</p>
          </div>
          {canSettle ? (
            <Button variant="primary" onClick={() => setShowCalc(true)} className="!bg-white !text-violet-700 hover:!bg-white/90">Calculate</Button>
          ) : null}
        </div>
      </div>

      {/* KPI Cards — using design system StatCard */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {isLoading || statsLoading ? (
          <>
            <Skeleton variant="rect" height={100} className="rounded-lg" />
            <Skeleton variant="rect" height={100} className="rounded-lg" />
            <Skeleton variant="rect" height={100} className="rounded-lg" />
          </>
        ) : (
          <>
            <StatCard title="Total Payable" value={formatPaise(pendingTotal, currency)} icon={payableIcon} />
            <StatCard title="Already Settled" value={formatPaise(paidTotal, currency)} icon={settledIcon} />
            <StatCard title="Pending Count" value={String(pendingCount)} icon={pendingIcon} />
          </>
        )}
      </div>

      {/* Filter bar */}
      <div className="rounded-xl bg-white dark:bg-neutral-800 shadow-md p-4 sm:p-5 ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 mb-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="w-full lg:w-44">
            {/* Named control with an "All Statuses" resting label — the empty
                value renders as the placeholder, so it doubles as the label for
                the unfiltered state and gives the Select an accessible name to
                match the adjacent "Settlement Period" picker (#165). */}
            <Select label="Status" placeholder="All Statuses" options={STATUS_OPTIONS} value={status} onChange={(v) => setParam('status', typeof v === 'string' ? v : '')} />
          </div>
          <div className="w-full lg:w-64">
            <DatePicker
              variant="range"
              label="Settlement Period"
              value={filterDateRange}
              onChange={handleFilterDateChange}
              maxDate={maxDate}
              placeholder="Select date range"
            />
          </div>
        </div>
      </div>

      {/* Data */}
      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} variant="rect" height={56} className="rounded-lg" />)}</div>
      ) : rows.length === 0 && !isFetching ? (
        <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md p-10 animate-fade-in">
          <EmptyState variant="page" title="No settlements" description="Use the Calculate button to generate commission records for a period."
            action={canSettle ? { label: 'Calculate Now', onClick: () => setShowCalc(true) } : undefined}
            icon={<svg className="h-20 w-20 text-violet-300 dark:text-violet-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3}><path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>}
          />
        </div>
      ) : (
        <section className="rounded-2xl bg-white dark:bg-neutral-800 shadow-md ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 overflow-hidden animate-slide-up">
          <DataTable<Row> columns={columns} data={rows} loading={isFetching} totalCount={data?.meta.total} currentPage={page} pageSize={20} onPageChange={(p) => setParam('page', String(p))} stickyHeader />
        </section>
      )}

      {/* Calculate Modal */}
      <Modal open={showCalc} onClose={() => setShowCalc(false)} title="Calculate Commissions" variant="wide">
        <div className="space-y-4">
          <Select
            label="Location"
            variant="searchable"
            options={locationOptions}
            value={calcLocationId}
            onChange={(v) => setCalcLocationId(typeof v === 'string' ? v : '')}
            placeholder="Search location by name…"
            required
          />
          <DatePicker
            variant="range"
            label="Calculation Period"
            value={calcDateRange}
            onChange={(v) => setCalcDateRange(v as DateRange)}
            maxDate={maxDate}
            placeholder="Select start and end dates"
          />
          <Select
            label="Staff Member (optional)"
            variant="searchable"
            options={userOptions}
            value={calcUserId}
            onChange={(v) => setCalcUserId(typeof v === 'string' ? v : '')}
            placeholder="All staff at location"
            clearable
          />
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setShowCalc(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleCalculate} loading={calculateMutation.isPending}>Calculate</Button>
          </div>
        </div>
      </Modal>

      {/* Mark Paid Modal — record a payment reference for the audit trail (#162) */}
      <Modal open={settleRow !== null} onClose={() => setSettleRow(null)} title="Mark Commission Paid">
        <div className="space-y-4">
          {settleRow ? (
            <p className="text-sm text-neutral-600 dark:text-neutral-300">
              Settling <span className="font-bold">{formatPaise(payableAmount(settleRow), currency)}</span> for{' '}
              <span className="font-bold">{settleRow.user_name ?? settleRow.user_id.slice(0, 8)}</span>.
            </p>
          ) : null}
          {/* Irreversibility warning — once PAID a settlement is terminal and
              cannot be reverted, so the confirmation must say so up front (#163). */}
          <div className="flex items-start gap-2 rounded-lg bg-warning-50 dark:bg-warning-900/20 p-3 ring-1 ring-warning-200 dark:ring-warning-800">
            <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning-600 dark:text-warning-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
            <p className="text-xs text-warning-800 dark:text-warning-200">
              This cannot be undone. Once marked Paid the settlement is final — there is no un-settle action.
            </p>
          </div>
          <RadioGroup
            label="Payment Mode"
            value={paymentMode}
            onChange={(v) => setPaymentMode(v as CommissionPaymentMode)}
            variant="horizontal"
            options={PAYMENT_MODE_OPTIONS}
          />
          <Input
            label="Payment Reference"
            value={paymentReference}
            onChange={(v) => { setPaymentReference(v); setSettleError(undefined); }}
            placeholder="UTR / cheque no. / transfer id"
            helperText="Required — proves how this commission was paid."
            error={settleError}
            required
          />
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setSettleRow(null)}>Cancel</Button>
            <Button variant="primary" onClick={handleSettle} loading={settleMutation.isPending}>Mark Paid</Button>
          </div>
        </div>
      </Modal>

      {/* Adjust Modal — an up/down correction of a pending settlement, with its history. */}
      <Modal open={adjustRow !== null} onClose={() => setAdjustRow(null)} title="Adjust settlement">
        <div className="space-y-4">
          {adjustRow ? (
            <p className="text-sm text-neutral-600 dark:text-neutral-300">
              Payable now <span className="font-bold">{formatPaise(payableAmount(adjustRow), currency)}</span> for{' '}
              <span className="font-bold">{adjustRow.user_name ?? adjustRow.user_id.slice(0, 8)}</span>.
            </p>
          ) : null}
          <Input
            label="Adjustment Amount"
            value={adjustAmount}
            onChange={(v) => { setAdjustAmount(v); setAdjustError(undefined); }}
            placeholder="e.g. 50 or -25"
            helperText="Positive raises the payable, negative lowers it."
            error={adjustError}
            required
          />
          <Input
            label="Reason"
            value={adjustReason}
            onChange={(v) => { setAdjustReason(v); setAdjustError(undefined); }}
            placeholder="e.g. missed bill, correction agreed with staff"
            required
          />
          {adjustHistory && adjustHistory.length > 0 ? (
            <div className="rounded-lg bg-neutral-50 dark:bg-neutral-900/40 p-3 ring-1 ring-neutral-100 dark:ring-neutral-700">
              <p className="text-xs font-bold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 mb-2">History</p>
              <ul className="space-y-1.5">
                {adjustHistory.map((a) => (
                  <li key={a.id} className="flex items-start justify-between gap-3 text-xs">
                    <span className="text-neutral-600 dark:text-neutral-300">
                      <Badge variant={a.adjustment_type === 'RETURN' ? 'voided' : 'pending'} size="sm">{a.adjustment_type === 'RETURN' ? 'Return' : 'Manual'}</Badge>{' '}
                      {a.reason ?? ''}
                    </span>
                    <span className={`tabular-nums font-bold ${Number(a.delta) < 0 ? 'text-danger-600 dark:text-danger-400' : 'text-success-600 dark:text-success-400'}`}>
                      {Number(a.delta) < 0 ? '−' : '+'}{formatPaise(Math.abs(Number(a.delta)), currency)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setAdjustRow(null)}>Close</Button>
            <Button variant="primary" onClick={handleAdjust} loading={adjustMutation.isPending}>Save Adjustment</Button>
          </div>
        </div>
      </Modal>

      {/* Cancel Settlement Modal — voids a pending payout (#160). */}
      <Modal open={cancelRow !== null} onClose={() => setCancelRow(null)} title="Cancel settlement">
        <div className="space-y-4">
          {cancelRow ? (
            <p className="text-sm text-neutral-600 dark:text-neutral-300">
              Cancel the <span className="font-bold">{formatPaise(payableAmount(cancelRow), currency)}</span> payout for{' '}
              <span className="font-bold">{cancelRow.user_name ?? cancelRow.user_id.slice(0, 8)}</span>? This voids the settlement — it won't be counted as payable and can't be marked Paid.
            </p>
          ) : null}
          <Input
            label="Reason (optional)"
            value={cancelReason}
            onChange={(v) => setCancelReason(v)}
            placeholder="e.g. duplicate calculation, wrong period"
          />
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={() => setCancelRow(null)}>Keep</Button>
            <Button
              variant="primary"
              onClick={handleCancel}
              loading={cancelMutation.isPending}
              className="!bg-danger-600 hover:!bg-danger-700"
            >
              Cancel settlement
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export function CommissionSettlementsPage() {
  return (
    <PageContainer title="Settlements" breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Commissions', href: '/commissions' }, { label: 'Settlements' }]}>
      <ErrorBoundary resetKey="commission-settlements">
        <SettlementsContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
